import React, { useState, useMemo } from 'react';

function confDotClass(confidence) {
  if (confidence === null || confidence === undefined) return 'qp-conf-dot-grey';
  if (confidence === 0) return 'qp-conf-dot-red';
  if (confidence === 3) return 'qp-conf-dot-green';
  return 'qp-conf-dot-amber';
}

export default function TopicPicker({ topics, selectedIds, onToggle }) {
  // topics: [{topic_id, subtopic, general_topic, subject_area, module, question_count, confidence}]
  // selectedIds: Set of selected topic_ids
  // onToggle: fn(topicId) — toggles a topic in/out of selection
  const [query, setQuery] = useState('');
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [expandedL2s, setExpandedL2s] = useState({});

  const filtered = useMemo(() => {
    if (!query) return topics;
    const q = query.toLowerCase();
    return topics.filter(t =>
      (t.subtopic || '').toLowerCase().includes(q) ||
      (t.general_topic || '').toLowerCase().includes(q) ||
      (t.subject_area || '').toLowerCase().includes(q)
    );
  }, [topics, query]);

  // Build hierarchy from filtered topics
  const hierarchy = useMemo(() => {
    const subjects = {};
    filtered.forEach(t => {
      const sa = t.subject_area || 'Other';
      if (!subjects[sa]) subjects[sa] = { label: sa, l2s: {}, totalCount: 0 };
      subjects[sa].totalCount += t.question_count || 0;
      const l2 = t.general_topic || 'General';
      if (!subjects[sa].l2s[l2]) subjects[sa].l2s[l2] = { label: l2, l3s: [], totalCount: 0 };
      subjects[sa].l2s[l2].totalCount += t.question_count || 0;
      subjects[sa].l2s[l2].l3s.push(t);
    });
    return subjects;
  }, [filtered]);

  const toggleSubject = (sa) => setExpandedSubjects(prev => ({ ...prev, [sa]: !prev[sa] }));
  const toggleL2 = (key) => setExpandedL2s(prev => ({ ...prev, [key]: !prev[key] }));

  // When searching, auto-expand all
  const isSearching = !!query;

  return (
    <div className="qp-picker">
      <input
        className="qp-topic-search"
        placeholder="Search topics"
        value={query}
        onChange={e => setQuery(e.target.value)}
      />
      <div className="qp-picker-list">
        {Object.entries(hierarchy).map(([sa, subjectData]) => {
          const isExpanded = isSearching || expandedSubjects[sa];
          return (
            <div key={sa} className="qp-picker-subject">
              <div
                className="qp-picker-subject-header"
                onClick={() => toggleSubject(sa)}
              >
                <span className="qp-picker-chevron">{isExpanded ? '▾' : '▸'}</span>
                <span className="qp-picker-subject-name">{sa}</span>
                <span className="qp-picker-count">{subjectData.totalCount}</span>
              </div>

              {isExpanded && (
                <div className="qp-picker-l2s">
                  {Object.entries(subjectData.l2s).map(([l2, l2Data]) => {
                    const l2Key = `${sa}::${l2}`;
                    const isL2Expanded = isSearching || expandedL2s[l2Key];
                    return (
                      <div key={l2Key} className="qp-picker-l2">
                        <div className="qp-picker-l2-row">
                          <button
                            className="qp-picker-expand-btn"
                            onClick={() => toggleL2(l2Key)}
                            title={isL2Expanded ? 'Collapse' : 'Expand subtopics'}
                          >
                            {isL2Expanded ? '▾' : '▸'}
                          </button>
                          <span className="qp-picker-l2-name">
                            {l2}
                          </span>
                          <span className="qp-picker-count">{l2Data.totalCount}</span>
                        </div>

                        {isL2Expanded && (
                          <div className="qp-picker-l3s">
                            {l2Data.l3s.map(t => {
                              const isSelected = selectedIds.has(t.topic_id);
                              return (
                                <div
                                  key={t.topic_id}
                                  className={`qp-picker-l3-row qp-picker-l3-row-multi ${isSelected ? 'qp-picker-l3-row-selected' : ''}`}
                                  onClick={() => onToggle(t.topic_id)}
                                >
                                  <input type="checkbox" className="qp-picker-checkbox" checked={isSelected} readOnly />
                                  <span className={`qp-conf-dot ${confDotClass(t.confidence)}`} />
                                  <span className="qp-picker-l3-name">{t.subtopic}</span>
                                  <span className="qp-picker-count">{t.question_count}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {Object.keys(hierarchy).length === 0 && (
          <div className="qp-picker-empty">No topics with questions found.</div>
        )}
      </div>
    </div>
  );
}
